import React, { useState, useEffect, useContext } from "react";
import Navbar from "../NavBar";
import NewSidebar from "../newSideBar";
import axios from "axios";
import { MyContext } from "../Context/SharedStateContext";
import { Link } from "react-router-dom";
import { Spinner } from "react-bootstrap";

const Dashboard = () => {
  const { setUserLimit, setRemoveBranding } = useContext(MyContext);
  const [activeTab, setActiveTab] = useState("Overview");
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const localURL = localStorage.getItem("shopURL");
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const tenantName = localStorage.getItem("tenantName");
  const [chatLoading, setChatLoading] = useState(true);

  // Check if user is a widget user
  const isWidgetUser = localStorage.getItem("isWidget") === "true";
  // const isWidgetUser =true;

  const headers = {
    "Content-Type": "application/json",
    "x-shop-url": localURL,
  };

  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
  const [checked, setChecked] = useState({
    enableAppEmbed: false,
    createFAQ: false,
  });

  const [open, setOpen] = useState({
    enableAppEmbed: false,
    createPopup: false,
    activatePopup: false,
  });

  const handleDescriptionToggle = (key) => {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isUserActive = (user) =>
    user.updatedAt && new Date(user.updatedAt).getTime() >= fiveMinutesAgo;

  const fetchEmbedStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/app-embed-status`,
        {
          headers: headers,
        }
      );

      if (response.data.enabled) {
        setChecked((prevState) => ({
          ...prevState,
          enableAppEmbed: true,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch app embed status:", error);
    } finally {
      setChatLoading(false);
    }
  };

  const fetchQA = async () => {
    try {
      const QAresponse = await axios.get(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/get-qa`,
        {
          headers: headers,
        }
      );
      setChecked((prev) => ({
        ...prev,
        createFAQ: QAresponse.data.length > 0,
      }));
    } catch (err) {
      console.error("Error Fetching Q&A", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/all-users`,
        { headers: headers }
      );
      const users = response.data.users || [];
      setUsers(users);
      setRemoveBranding(response.data.removeBranding || false);

      const activeUsers = users.filter(
        (user) =>
          user.updatedAt && new Date(user.updatedAt).getTime() >= fiveMinutesAgo
      ).length;
      setActiveUsersCount(activeUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/store-order`,
        {
          headers: headers,
        }
      );
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  useEffect(() => {
    // Set initial tab based on user type
    if (isWidgetUser) {
      setActiveTab("Users");
    }

    fetchQA();
    fetchUsers();

    // Only fetch orders and users for Shopify users
    if (!isWidgetUser) {
      fetchEmbedStatus();
      fetchOrders();
    } else {
      setChatLoading(false);
    }
  }, [isWidgetUser]);

  // Categorizing orders dynamically (only for Shopify users)
  const categorizedOrders = {
    paid: orders.filter((order) => order.displayFinancialStatus === "PAID"),
    delivered: orders.filter(
      (order) => order.displayFulfillmentStatus === "FULFILLED"
    ),
    pending: orders.filter(
      (order) => order.displayFinancialStatus === "PENDING"
    ),
    canceled: orders.filter(
      (order) =>
        order.displayFinancialStatus === "Refunded" ||
        order.displayFinancialStatus === "Voided"
    ),
  };

  /* Helper function for status classes */
  function getStatusClass(status) {
    return status.toLowerCase().replace(/\s+/g, "-");
  }

  /* Helper functions */
  function getUserStatusClass(status) {
    switch (status) {
      case 1:
        return "unassigned";
      case 2:
        return "myopen";
      case 3:
        return "solved";
      default:
        return "unknown";
    }
  }

  function getUserStatusText(status) {
    switch (status) {
      case 1:
        return "Unassigned";
      case 2:
        return "MyOpen";
      case 3:
        return "Solved";
      default:
        return "Unknown";
    }
  }

  // Tab content definitions
  const tabContent = {
    Users: (
      <>
        {/* User Stats Cards */}
        <div className="user-dashboard-stats-container">
          {[
            {
              title: "Total Users",
              value: users.length,
              icon: "mdi-account-group",
              footerText: "Registered Users",
              colorClass: "user-stats-card-primary",
            },
            {
              title: "Active Users",
              value: activeUsersCount,
              icon: "mdi-account-check",
              footerText: "Active",
              colorClass: "user-stats-card-success",
            },
          ].map((item, index) => (
            <div className="user-stats-col" key={index}>
              <div className="user-stats-card">
                <div className="user-stats-card-body">
                  <div className="user-stats-header">
                    <h5 className="user-stats-title">{item.title}</h5>
                    <div className={`user-stats-icon ${item.colorClass}`}>
                      <i
                        className={`mdi ${item.icon} user-stats-icon-symbol`}
                      ></i>
                    </div>
                  </div>
                  <h2 className="user-stats-value">{item.value}</h2>
                  <p className="user-stats-footer">{item.footerText}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Users Table */}
        <div className="user-table-container">
          <div className="user-table-card">
            <div className="user-table-header">
              <h4 className="user-table-title">Users</h4>
            </div>
            <div className="user-table-wrapper">
              <table className="user-table">
                <thead className="user-table-head">
                  <tr>
                    <th className="user-table-th">User ID</th>
                    <th className="user-table-th">Name</th>
                    <th className="user-table-th">Email</th>
                    <th className="user-table-th">Status</th>
                    <th className="user-table-th">Created At</th>
                  </tr>
                </thead>
                <tbody className="user-table-body">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="user-table-empty">
                        No Users Found
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} className="user-table-row">
                        <td className="user-table-td">
                          <div className="user-identifier">
                            <span className="user-id">{user.id}</span>
                            {isUserActive(user) && (
                              <span className="user-active-indicator"></span>
                            )}
                          </div>
                        </td>
                        <td className="user-table-td">{user.name || "N/A"}</td>
                        <td className="user-table-td">{user.email}</td>
                        <td className="user-table-td">
                          <span
                            className={`user-status user-status-${getUserStatusClass(
                              user.status
                            )}`}
                          >
                            {getUserStatusText(user.status)}
                          </span>
                        </td>
                        <td className="user-table-td">
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : "N/A"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    ),
    Orders: (
      <>
        {/* Stats Cards */}
        <div className="order-stats-container">
          {[
            {
              title: "Total Orders",
              value: orders.length,
              icon: "mdi-cart",
              footerText: "Total Orders",
              colorClass: "order-stats-card-primary",
            },
            {
              title: "Paid Orders",
              value: categorizedOrders.paid.length,
              icon: "mdi-cash",
              footerText: "Paid",
              colorClass: "order-stats-card-success",
            },
            {
              title: "Delivered Orders",
              value: categorizedOrders.delivered.length,
              icon: "mdi-truck",
              footerText: "Delivered",
              colorClass: "order-stats-card-info",
            },
            {
              title: "Pending Orders",
              value: categorizedOrders.pending.length,
              icon: "mdi-timer-sand",
              footerText: "Pending",
              colorClass: "order-stats-card-warning",
            },
          ].map((item, index) => (
            <div className="order-stats-col" key={index}>
              <div className="order-stats-card">
                <div className="order-stats-card-body">
                  <div className="order-stats-header">
                    <h6 className="order-stats-title">{item.title}</h6>
                    <div className={`order-stats-icon ${item.colorClass}`}>
                      <i
                        className={`mdi ${item.icon} order-stats-icon-symbol`}
                      ></i>
                    </div>
                  </div>
                  <h2 className="order-stats-value">{item.value}</h2>
                  <p className="order-stats-footer">{item.footerText}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Orders Table */}
        <div className="order-table-container">
          <div className="order-table-card">
            <div className="order-table-header">
              <h4 className="order-table-title">Recent Orders</h4>
              <div className="order-table-filter">
                <button className="order-table-filter-btn" type="button">
                  Filter <i className="mdi mdi-chevron-down"></i>
                </button>
              </div>
            </div>
            <div className="order-table-wrapper">
              <table className="order-table">
                <thead className="order-table-head">
                  <tr>
                    <th className="order-table-th">Order ID</th>
                    <th className="order-table-th">Customer</th>
                    <th className="order-table-th">Amount</th>
                    <th className="order-table-th">Status</th>
                    <th className="order-table-th">Created At</th>
                  </tr>
                </thead>
                <tbody className="order-table-body">
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="order-table-empty">
                        No Orders Found
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr key={order.id} className="order-table-row">
                        <td className="order-table-td">
                          <a href="#" className="order-table-link">
                            #{order.name}
                          </a>
                        </td>
                        <td className="order-table-td">
                          {order.email || "Guest"}
                        </td>
                        <td className="order-table-td">
                          {order.totalPriceSet.shopMoney.amount}{" "}
                          {order.totalPriceSet.shopMoney.currencyCode}
                        </td>
                        <td className="order-table-td">
                          <span
                            className={`order-status-badge order-status-${getStatusClass(
                              order.displayFinancialStatus
                            )}`}
                          >
                            {order.displayFinancialStatus}
                          </span>
                        </td>
                        <td className="order-table-td">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="order-table-footer">
              <div className="order-table-summary">
                Showing <b>1</b> to <b>{orders.length}</b> of {orders.length}{" "}
                entries
              </div>
              <div className="order-table-pagination">
                <button className="order-table-pagination-btn disabled">
                  Previous
                </button>
                <button className="order-table-pagination-btn active">1</button>
                <button className="order-table-pagination-btn">2</button>
                <button className="order-table-pagination-btn">3</button>
                <button className="order-table-pagination-btn">Next</button>
              </div>
            </div>
          </div>
        </div>
      </>
    ),
    Overview: (
      <div>
        {/* App Setup Card */}
        <div className="app-setup-container">
          <h2 className="title">App Setup</h2>
          <p className="subtitle">
            Follow the steps below to configure your app
          </p>

          {/* Enable App Embed */}
          <div className="setup-box">
            <div className="setup-header">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  checked={checked.enableAppEmbed}
                  readOnly
                  className="checkbox"
                />
                <span className="checkbox-label">Enable App Embed</span>
              </div>
              <button
                onClick={() => handleDescriptionToggle("enableAppEmbed")}
                className="info-button"
              >
                <span className="info-icon">i</span>
                <span className="info-text">What is this?</span>
              </button>
            </div>
            {open.enableAppEmbed && (
              <div className="setup-content">
                <p className="description">
                  To display the Chat Icon on your store, enable the Epic-X Chat
                  app embed in your theme. The Chat will not appear until
                  activated.
                </p>
                <button
                  onClick={() =>
                    window.open(
                      `https://${localURL}/admin/themes/current/editor?context=apps`
                    )
                  }
                  className="action-button"
                >
                  Enable App Embed
                </button>
              </div>
            )}
          </div>

          {/* Create Popup */}
          <div className="setup-box">
            <div className="setup-header">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  checked={checked.createFAQ}
                  readOnly
                  className="checkbox"
                />
                <span className="checkbox-label">Create FAQ and Answers</span>
              </div>
              <button
                onClick={() => handleDescriptionToggle("createPopup")}
                className="info-button"
              >
                <span className="info-icon">i</span>
                <span className="info-text">What is this?</span>
              </button>
            </div>
            {open.createPopup && (
              <div className="setup-content">
                <p className="description">
                  Our AI bot will automatically respond to user questions based
                  on the FAQs you create.
                </p>
                <Link
                  to={`/${tenantName}/chat/faq-bot`}
                  className="action-button"
                >
                  Create FAQ
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Additional Information Section Outside the Card */}
        <div className="info-section">
          <h3 className="info-title">More Features</h3>
          <ul className="info-list">
            <li>
              <span className="feature-icon">🔹</span>
              <strong>1 to 1 Conversation:</strong> Directly chat with customers
              in real-time.
            </li>
            <li>
              <span className="feature-icon">🔹</span>
              <strong>FAQ Bot:</strong> Automate responses using AI-powered FAQ
              assistance.
            </li>
            <li>
              <span className="feature-icon">🔹</span>
              <strong>Customize Chat Appearance:</strong> Personalize chat
              design to match your brand.
            </li>
            <li>
              <span className="feature-icon">🔹</span>
              <strong>Set Display Rule for Chat:</strong> Control when and where
              the chat appears.
            </li>
            <li>
              <span className="feature-icon">🔹</span>
              <strong>Set Admin Active Hour:</strong> Configure availability to
              respond to chats efficiently.
            </li>
          </ul>
        </div>
      </div>
    ),
    Install: (
      <div className="install-container">
        <div className="installation-methods">
          {/* HTML Method */}
          <div className="method-card">
            <div className="method-header">
              <h3 className="method-title">
                <i className="mdi mdi-language-html5 method-icon"></i>
                For HTML Websites
              </h3>
              <button
                className="copy-button"
                onClick={() => {
                  const htmlCode = `<script>
window.EpicXChatConfig = {
  api: {
    shopDomain: '${localURL || "your-domain"}'
  }
};
</script>
<script src="https://epic-x.io/widget.js"></script>`;
                  navigator.clipboard.writeText(htmlCode);
                  // You can add a toast notification here
                  alert("HTML code copied to clipboard!");
                }}
              >
                <i className="mdi mdi-content-copy"></i>
                Copy Code
              </button>
            </div>
            <div className="method-content">
              <p className="method-description">
                Add the following code to your website's{" "}
                <code>&lt;head&gt;</code> section:
              </p>
              <div className="code-container">
                <pre className="code-block">
                  {`<script>
window.EpicXChatConfig = {
  api: {
    shopDomain: '${localURL || "your-domain"}'
  }
};
</script>
<script src="https://epic-x.io/widget.js"></script>`}
                </pre>
              </div>
              {/* <div className="method-note">
                <strong>Note:</strong> Replace <code>'your-domain'</code> with
                your actual domain name.
              </div> */}
            </div>
          </div>

          {/* React Method */}
          <div className="method-card">
            <div className="method-header">
              <h3 className="method-title">
                <i className="mdi mdi-react method-icon"></i>
                For React Applications
              </h3>
              <button
                className="copy-button"
                onClick={() => {
                  const reactCode = `import { useEffect } from 'react';

const YourComponent = () => {
  useEffect(() => {
    // Load the widget script
    const script = document.createElement('script');
    script.src = 'https://epic-x.io/widget.js';
    script.async = true;
    
    // Add configuration
    const configScript = document.createElement('script');
    configScript.textContent = \\\`
      window.EpicXChatConfig = {
        api: {
          shopDomain: '${localURL || "your-domain"}'
        }
      };
    \\\`;
    
    document.head.appendChild(configScript);
    document.body.appendChild(script);

    return () => {
      // Cleanup on component unmount
      document.head.removeChild(configScript);
      document.body.removeChild(script);
      
      // Remove widget elements
      const widget = document.getElementById('epic-x-chat-open-chat');
      const chatbox = document.getElementById('epic-x-chat-chatbox');
      if (widget) widget.remove();
      if (chatbox) chatbox.remove();
    };
  }, []);

  return (
    // Your component JSX
  );
};`;
                  navigator.clipboard.writeText(reactCode);
                  // You can add a toast notification here
                  alert("React code copied to clipboard!");
                }}
              >
                <i className="mdi mdi-content-copy"></i>
                Copy Code
              </button>
            </div>
            <div className="method-content">
              <p className="method-description">
                Add the following code to your main component or App.js:
              </p>
              <div className="code-container">
                <pre className="code-block">
                  {`import { useEffect } from 'react';

const YourComponent = () => {
  useEffect(() => {
    // Load the widget script
    const script = document.createElement('script');
    script.src = 'https://epic-x.io/widget.js';
    script.async = true;
    
    // Add configuration
    const configScript = document.createElement('script');
    configScript.textContent = \\\`window.EpicXChatConfig = {
        api: {
          shopDomain: '${localURL || "your-domain"}'
        }
      };\\\`;
    
    document.head.appendChild(configScript);
    document.body.appendChild(script);

    return () => {
      // Cleanup on component unmount
      document.head.removeChild(configScript);
      document.body.removeChild(script);
      
      // Remove widget elements
      const widget = document.getElementById('epic-x-chat-open-chat');
      const chatbox = document.getElementById('epic-x-chat-chatbox');
      if (widget) widget.remove();
      if (chatbox) chatbox.remove();
    };
  }, []);

  return (
    // Your component JSX
  );
};`}
                </pre>
              </div>
              {/* <div className="method-note">
                <strong>Note:</strong> Replace <code>'your-domain'</code> with
                your actual domain name.
              </div> */}
            </div>
          </div>
        </div>

        <div className="widget-features">
          <h3 className="features-title">Widget Features</h3>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">💬</div>
              <div className="feature-content">
                <h4>Real-time Chat</h4>
                <p>Engage with your website visitors in real-time</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🤖</div>
              <div className="feature-content">
                <h4>AI-Powered FAQ</h4>
                <p>Automated responses based on your FAQ knowledge base</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🎨</div>
              <div className="feature-content">
                <h4>Customizable</h4>
                <p>Match the widget appearance with your brand</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">⚡</div>
              <div className="feature-content">
                <h4>Easy Setup</h4>
                <p>Simple installation with just a few lines of code</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  };

  // Define tabs based on user type
  const getTabs = () => {
    if (isWidgetUser) {
      return ["Users", "Install"];
    } else {
      return ["Overview", "Users", "Orders"];
    }
  };

  const tabs = getTabs();

  return (
    <div className="d-flex flex-column w-100">
      <Navbar />
      <div className="d-flex w-100">
        <NewSidebar />
        <div
          className="main-panel"
          style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
        >
          {chatLoading ? (
            <div
              style={{
                height: "100%",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "#f0f1f6",
              }}
            >
              <Spinner
                animation="border"
                variant="primary"
                style={{ width: "3rem", height: "3rem" }}
              />
            </div>
          ) : (
            <>
              <div className="content-wrapper">
                <div className="d-xl-flex justify-content-between align-items-start">
                  <h2
                    className="text-dark font-weight-bold mb-2"
                    style={{ fontWeight: 600 }}
                  >
                    {isWidgetUser ? "Widget Dashboard" : "Overview Dashboard"}
                  </h2>
                </div>
                <div className="dashboard-container">
                  <div className="dashboard-tab-header">
                    <div className="dashboard-tab-nav">
                      <ul className="dashboard-tab-list" role="tablist">
                        {tabs.map((tab) => (
                          <li className="dashboard-tab-item" key={tab}>
                            <button
                              className={`dashboard-tab-button ${
                                activeTab === tab ? "dashboard-tab-active" : ""
                              }`}
                              onClick={() => setActiveTab(tab)}
                            >
                              <span className="dashboard-tab-icon">
                                {tab === "Overview" && (
                                  <i className="mdi mdi-view-dashboard"></i>
                                )}
                                {tab === "Users" && (
                                  <i className="mdi mdi-account-group"></i>
                                )}
                                {tab === "Orders" && (
                                  <i className="mdi mdi-cart"></i>
                                )}
                                {tab === "Install" && (
                                  <i className="mdi mdi-download"></i>
                                )}
                              </span>
                              {tab}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="dashboard-tab-content">
                    {tabContent[activeTab]}
                  </div>
                </div>
              </div>
              {/* Footer */}
              <footer className="footer">
                <div className="footer-inner-wraper">
                  <div className="d-sm-flex justify-content-center justify-content-sm-between py-2">
                    <span className="text-muted text-center text-sm-left d-block d-sm-inline-block">
                      Copyright ©{" "}
                      <a
                        href="https://epic-x.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Epic-x.io{" "}
                      </a>
                      2025
                    </span>
                    <span className="float-none float-sm-right d-block mt-1 mt-sm-0 text-center">
                      Developed By{" "}
                      <a
                        href="https://epic-x.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Epic-X
                      </a>
                    </span>
                  </div>
                </div>
              </footer>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
